import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import BrandLogo from '../../components/common/BrandLogo';
import { MdLock } from 'react-icons/md';
import useAuth from '../../hooks/useAuth';
import { useTenant } from '../../context/TenantContext';
import api from '../../services/api';

export default function ChangePassword() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword,     setNewPassword]     = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent,     setShowCurrent]     = useState(false);
  const [showNew,         setShowNew]         = useState(false);
  const [showConfirm,     setShowConfirm]     = useState(false);
  const [loading,         setLoading]         = useState(false);
  const [error,           setError]           = useState('');

  const { user, updateUser, getDashboardPath } = useAuth();
  const { club }                               = useTenant();
  const navigate                               = useNavigate();

  const accent = club?.primaryColor || '#E24B4A';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!currentPassword) {
      return setError('Please enter your current password');
    }
    if (newPassword.length < 8) {
      return setError('Password must be at least 8 characters');
    }
    if (newPassword !== confirmPassword) {
      return setError('Passwords do not match');
    }

    setLoading(true);
    try {
      await api.post('/api/auth/change-password', { currentPassword, newPassword });
      // Clear the mustChangePassword flag from local user state
      updateUser({ mustChangePassword: false });
      toast.success('Password set! Welcome to TrainAxis.');
      navigate(getDashboardPath(user?.role), { replace: true });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to change password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const strength = newPassword.length >= 12 ? 'Strong' : newPassword.length >= 8 ? 'Good' : newPassword.length > 0 ? 'Too short' : '';
  const strengthColor = strength === 'Strong' ? 'text-green-600' : strength === 'Good' ? 'text-blue-600' : 'text-red-500';

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 bg-[#141312]">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <BrandLogo size={48} className="mb-3" />
          <span className="text-sm font-semibold text-[#8a847c]">TrainAxis</span>
        </div>

        {/* Icon */}
        <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-amber-50 mx-auto mb-5">
          <MdLock className="text-amber-500 text-3xl" />
        </div>

        <h1 className="text-2xl font-bold text-[#ede8e0] text-center mb-2">Set Your Password</h1>
        <p className="text-sm text-[#8a847c] text-center mb-8">
          Welcome! Please choose a new password before continuing.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          {/* Current (temporary) Password */}
          <div>
            <label className="block text-sm font-medium text-[#ede8e0] mb-1.5">Current Password</label>
            <div className="relative">
              <input
                type={showCurrent ? 'text' : 'password'}
                value={currentPassword}
                onChange={e => { setCurrentPassword(e.target.value); setError(''); }}
                placeholder="The password you signed in with"
                required
                autoFocus
                autoComplete="current-password"
                className="w-full px-3.5 py-2.5 pr-11 text-sm bg-[#1a1918] border border-[rgba(237,232,224,0.08)] rounded-xl text-[#ede8e0] placeholder-[#6b6560] outline-none transition-all focus:border-[rgba(237,232,224,0.15)] focus:outline-none"
                onFocus={e => (e.target.style.borderColor = accent)}
                onBlur={e  => (e.target.style.borderColor = error ? '#fca5a5' : '#e5e7eb')}
              />
              <button
                type="button"
                onClick={() => setShowCurrent(p => !p)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8a847c] hover:text-[#ede8e0]"
              >
                {showCurrent ? <FaEyeSlash size={15} /> : <FaEye size={15} />}
              </button>
            </div>
          </div>

          {/* New Password */}
          <div>
            <label className="block text-sm font-medium text-[#ede8e0] mb-1.5">New Password</label>
            <div className="relative">
              <input
                type={showNew ? 'text' : 'password'}
                value={newPassword}
                onChange={e => { setNewPassword(e.target.value); setError(''); }}
                placeholder="Min. 8 characters"
                required
                autoComplete="new-password"
                className="w-full px-3.5 py-2.5 pr-11 text-sm bg-[#1a1918] border border-[rgba(237,232,224,0.08)] rounded-xl text-[#ede8e0] placeholder-[#6b6560] outline-none transition-all focus:border-[rgba(237,232,224,0.15)] focus:outline-none"
                onFocus={e => (e.target.style.borderColor = accent)}
                onBlur={e  => (e.target.style.borderColor = error ? '#fca5a5' : '#e5e7eb')}
              />
              <button
                type="button"
                onClick={() => setShowNew(p => !p)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8a847c] hover:text-[#ede8e0]"
              >
                {showNew ? <FaEyeSlash size={15} /> : <FaEye size={15} />}
              </button>
            </div>
            {strength && (
              <p className={`text-xs mt-1 font-medium ${strengthColor}`}>{strength}</p>
            )}
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-sm font-medium text-[#ede8e0] mb-1.5">Confirm Password</label>
            <div className="relative">
              <input
                type={showConfirm ? 'text' : 'password'}
                value={confirmPassword}
                onChange={e => { setConfirmPassword(e.target.value); setError(''); }}
                placeholder="Repeat your password"
                required
                className="w-full px-3.5 py-2.5 pr-11 text-sm bg-[#1a1918] border border-[rgba(237,232,224,0.08)] rounded-xl text-[#ede8e0] placeholder-[#6b6560] outline-none transition-all focus:border-[rgba(237,232,224,0.15)] focus:outline-none"
                onFocus={e => (e.target.style.borderColor = accent)}
                onBlur={e  => (e.target.style.borderColor = error ? '#fca5a5' : '#e5e7eb')}
              />
              <button
                type="button"
                onClick={() => setShowConfirm(p => !p)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8a847c] hover:text-[#ede8e0]"
              >
                {showConfirm ? <FaEyeSlash size={15} /> : <FaEye size={15} />}
              </button>
            </div>
          </div>

          {/* Error */}
          {error && (
            <p className="text-sm text-red-400 bg-red-400/10 rounded-xl px-3 py-2">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 bg-primary text-white hover:bg-primary-dark rounded-full font-medium text-sm transition-opacity disabled:opacity-60 flex items-center justify-center mt-2"
            style={{ background: accent }}
          >
            {loading
              ? <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              : 'Set Password & Continue'}
          </button>
        </form>
      </div>
    </div>
  );
}
